import React, {createContext, useContext, useState} from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {createTheme} from "@mui/material";

const ThemeContext = createContext();

export const CustomThemeProvider = ({children}) => {
  const [currentTheme, setCurrentTheme] = useState(getThemeFromLocalStorage());

  const getTheme = () => {
    switch (currentTheme) {
      case 'dark':
        return getMainTheme(darkPalette);
      case 'barbie':
        return getMainTheme(barbieTheme);
      case 'meadow':
        return getMainTheme(meadowPalette);
      default:
        return getMainTheme(defaultPalette);
    }
  };

  const toggleTheme = (themeName) => {
    setThemeToLocalStorage(themeName);

    setCurrentTheme(themeName);
  };

  return (
    <ThemeContext.Provider value={{currentTheme, toggleTheme}}>
      <ThemeProvider theme={getTheme()}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

const fontSizeScale = {
  small: {
    fontSize11: '8px',
    fontSize12: '10px',
    fontSize13: '11px',
    fontSize14: '12px',
    fontSize16: '14px',
    fontSize18: '16px',
  },
  normal: {
    fontSize11: '11px',
    fontSize12: '12px',
    fontSize13: '13px',
    fontSize14: '14px',
    fontSize16: '16px',
    fontSize18: '18px',
  },
  large: {
    fontSize14: '16px',
    fontSize16: '18px',
    fontSize18: '20px',
  },
  xlarge: {
    fontSize14: '18px',
    fontSize16: '20px',
    fontSize18: '22px',
  }
}
const defaultFontSizeScale = 'normal'
const defaultFontFamily = ["Roboto", "Helvetica", "Arial", "sans-serif"]

const defaultPalette = {
  primary: {
    main: '#1565c0e0',
  },
  info: {
    main: '#01579b',
  },
  error: {
    main: '#c62828',
  },
  warning: {
    main: '#ed6c02'
  },
  secondary: {
    main: '#ccc'
  },
  text: {
    primary: '#000000de',
    default: '#fff',
    secondary: '#666666',
    success: '#2e7d32',
    disabled: '#ccc',           // добавлено из darkPalette
    link: '#1565c0',            // добавлено из darkPalette
    table: {
      default: '#333333',
    },
    tableHead: {
      default: '#333333',
    },
  },
  background: {
    default: '#fff',
    secondary: '#f7f7f7',
    paper: '#fff'               // добавлено из darkPalette
  },
  border: {
    default: '#e3e3e3',
    secondary: '#e3e3e3'        // добавлено из darkPalette
  },
  icon: {
    default: '#999999',
    secondary: '#dadada'
  },
  button: {
    text: {
      default: '',
      secondary: '#333333',
    },
    background: {
      secondary: '#e3e3e3',
      hoverSecondary: '#dadada',
    },
    border: {
      default: ''        // добавлено из darkPalette
    }
  }
};
const darkPalette = {
  mode: "dark",
  primary: {
    main: '#1565c0',
  },
  info: {
    main: '#01579b',
  },
  error: {
    main: '#c62828',
  },
  warning: {
    main: '#ed6c02'
  },
  secondary: {
    main: '#ccc'
  },
  text: {
    primary: '#ccc',
    default: '#ccc',
    secondary: '#666666',
    success: '#2e7d32',
    disabled: '#ccc',
    link: '#29B6F6',
    table: {
      default: '#ccc',
    },
    tableHead: {
      default: '#ccc',
    },
  },
  background: {
    default: '#1e1e1e',
    secondary: '#121212',
    paper: '#121212'
  },
  border: {
    default: '#5a5a5a',
    secondary: '#5a5a5a'
  },
  icon: {
    default: '#999999',
    secondary: '#dadada'
  },
  button: {
    text: {
      default: '',
      secondary: '#333333',
    },
    background: {
      secondary: '#e3e3e3',
      hoverSecondary: '#dadada',
    },
    border: {
      default: '#5a5a5a'
    }
  }
};
const barbieTheme = {
  mode: "light",
  primary: {
    main: '#e0218a',      // Яркий розовый Барби
    light: '#ff4da6',
    dark: '#c21867',
  },
  secondary: {
    main: '#00c7e6',      // Бирюзовый акцент
    light: '#40d4ff',
    dark: '#0099b8',
  },
  info: {
    main: '#ff6bb5',      // Светлый розовый
  },
  error: {
    main: '#ff3366',      // Розово-красный
  },
  warning: {
    main: '#ffd700'       // Золотой
  },
  success: {
    main: '#00d4aa',      // Мятный
  },
  text: {
    primary: '#2d1b69',   // Темно-фиолетовый
    secondary: '#e0218a', // Розовый Барби
    disabled: '#ffb6e1',  // Светлый розовый
    success: '#00d4aa',
    link: '#00d4aa',
    table: {
      default: '#2d1b69',
    },
    tableHead: {
      default: '#e0218a', // Розовые заголовки
    },
  },
  background: {
    default: '#fff0f5',   // Очень светлый розовый (lavenderblush)
    secondary: '#ffe4ec', // Светлый розовый
    paper: '#ffffff'      // Белый
  },
  border: {
    default: '#ff69b4',   // Hot pink
    secondary: '#ffb6c1', // Light pink
    accent: '#00c7e6'     // Бирюзовый
  },
  icon: {
    default: '#e0218a',   // Розовые иконки
    secondary: '#ff6bb5', // Светлые розовые
    accent: '#00c7e6'     // Бирюзовые акценты
  },
  button: {
    text: {
      default: '#ffffff',
      secondary: '#2d1b69',
    },
    background: {
      primary: '#e0218a',
      secondary: '#ff6bb5',
      hoverPrimary: '#c21867',
      hoverSecondary: '#ff4da6',
      accent: '#00c7e6',    // Бирюзовые кнопки
    },
    border: {
      default: '#e0218a',
      secondary: '#ff6bb5',
      accent: '#00c7e6'
    }
  },
  divider: '#ffb6c1',     // Светло-розовые разделители
  barbie: {
    pink: '#e0218a',      // Основной розовый
    hotPink: '#ff69b4',   // Яркий розовый
    lightPink: '#ffb6c1', // Светлый розовый
    purple: '#dda0dd',    // Сливовый
    turquoise: '#00c7e6', // Бирюзовый
    gold: '#ffd700'       // Золотой
  }
};
const meadowPalette = {
  primary: {
    main: '#2e7d32', // насыщенный зеленый (сочная трава)
  },
  info: {
    main: '#0288d1', // небесно-голубой (ясное небо)
  },
  error: {
    main: '#d32f2f', // теплый красный (маковые цветы)
  },
  warning: {
    main: '#f57c00'  // золотистый (солнечные лучи)
  },
  secondary: {
    main: '#7cb342' // свежий зеленый (молодая трава)
  },
  text: {
    primary: '#1b5e20',    // темно-зеленый (глубина леса)
    default: '#ffffff',    // белый (облака)
    secondary: '#689f38',  // оливково-зеленый
    success: '#388e3c',    // ярко-зеленый (успех)
    disabled: '#c8e6c9',   // светло-салатовый
    link: '#2e7d32',       // основной зеленый для ссылок
    table: {
      default: '#1b5e20',  // темно-зеленый
    },
    tableHead: {
      default: '#ffffff',  // белый на зеленом фоне
    },
  },
  background: {
    default: '#f1f8e9',    // очень светлый салатовый (утренняя роса)
    secondary: '#e8f5e8',  // светло-зеленый (свежая трава)
    paper: '#ffffff'       // белый (лепестки ромашек)
  },
  border: {
    default: '#c5e1a5',    // мягкий зеленый (стебли травы)
    secondary: '#dcedc8'   // светлый зеленый
  },
  icon: {
    default: '#689f38',    // оливково-зеленый
    secondary: '#aed581'   // салатовый
  },
  button: {
    text: {
      default: '#ffffff',
      secondary: '#1b5e20', // темно-зеленый
    },
    background: {
      secondary: '#c8e6c9', // нежный зеленый
      hoverSecondary: '#a5d6a7', // более насыщенный зеленый при наведении
    },
    border: {
      default: '#2e7d32'   // основной зеленый
    }
  }
};

export const getMainTheme = (palette) => {
  return createTheme({
    palette,
    // Мои компоненты
    Title5: {
      fontWeight: 400,
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize16,
      fontFamily: defaultFontFamily,
      margin: 0,
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    Title4: {
      fontWeight: 400,
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize18,
      fontFamily: defaultFontFamily,
      margin: 0,
      lineHeight: 1.6,
      letterSpacing: '0.0075em',
    },
    PlainText: {
      letterSpacing: '0.14994px',
      lineHeight: '20.02px',
      fontWeight: 400,
      margin: 0,
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize14,
      fontFamily: defaultFontFamily,
    },
    PlainTextSmall: {
      letterSpacing: '0.14994px',
      lineHeight: '20.02px',
      fontWeight: 400,
      margin: 0,
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize12,
      fontFamily: defaultFontFamily,
    },
    Link1: {
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize14,
      textDecoration: "underline"
    },
    Link2: {
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize16,
      textDecoration: "none"
    },
    Button: {
      fontSize: fontSizeScale[defaultFontSizeScale].fontSize13,
      textDecoration: "none"
    },
    components: {
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: fontSizeScale[defaultFontSizeScale].fontSize14,
          },
          secondary: {
            fontSize: fontSizeScale[defaultFontSizeScale].fontSize14,
          },
        },
      }
    },
  });
};

const getThemeFromLocalStorage = () => {
  return localStorage.getItem('themeName') || 'default'
}

const setThemeToLocalStorage = (themeName) => {
  localStorage.setItem('themeName', themeName);
}

export const useCustomeTheme = () => useContext(ThemeContext);